import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Paper,
	Typography,
	Grid,
	Checkbox,
	FormControlLabel,
	CircularProgress,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import { useQuery, useQueryClient } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { getJobCodes } from 'core/API/scheduler';
import { getJobCodeIdFromUserJobCode, getUserJobCodes } from 'core/API/userJobCodes';
import { apiService } from 'core/services/apiService';
import { UserAvailability } from 'core/interfaces/userInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { Button } from 'components/ui/button';
import CurrentAvailabilityList from 'module/schedule/components/CurrentAvailabilityList/CurrentAvailabilityList';
import EligibleJobCodesPicker from 'module/schedule/components/EligibleJobCodesPicker/EligibleJobCodesPicker';
import { buildAllWeekDays } from 'module/schedule/utils/availabilityDisplay';
import {
	buildOnboardingAvailabilityInitialValues,
	extractUserAvailabilityFromUser,
	hasSavedAvailability,
	saveOnboardingAvailability,
} from '../utils/onboardingAvailability';
import style from './EmployeeOnboardingAvailabilityForm.module.scss';

interface EmployeeOnboardingAvailabilityFormProps {
	workplaceId: number;
	editable: boolean;
	onSubmit: () => void;
}

export function EmployeeOnboardingAvailabilityForm({
	workplaceId,
	editable,
	onSubmit,
}: EmployeeOnboardingAvailabilityFormProps): JSX.Element {
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedJobCodeIds, setSelectedJobCodeIds] = useState<number[]>([]);
	const [jobCodesInitialized, setJobCodesInitialized] = useState(false);

	const { data: daysData, isLoading: daysLoading } = useQuery(['days'], () => getDays());
	const allDays = useMemo(() => buildAllWeekDays(daysData), [daysData]);

	const { data: jobCodesData = [], isLoading: jobCodesLoading } = useQuery(
		'jobCodes',
		() => getJobCodes(),
		{ staleTime: 5 * 60 * 1000 },
	);

	const authUserId = Number(localStorage.getItem('userId') || 0);

	// Prefill from the User resource (`userAvailability`), including backend defaults
	// created after basic info. Do not GET `/user_availabilities` for display.
	const {
		data: userData,
		isLoading: userLoading,
		refetch: refetchUser,
	} = useQuery(
		['user-availability', authUserId || 'me', 'onboarding'],
		async () => {
			const endpoint =
				Number.isFinite(authUserId) && authUserId > 0
					? `/users/${authUserId}`
					: '/users/me';
			const response = await apiService.get(endpoint);
			return apiService.responseHandler(response);
		},
		{
			refetchOnMount: 'always',
			staleTime: 0,
		},
	);

	const userId = Number(userData?.id) || authUserId;

	const { data: userJobCodes = [], isLoading: userJobCodesLoading } = useQuery(
		['user-job-codes', 'me', userId],
		() => getUserJobCodes({ userId }),
		{
			enabled: Number.isFinite(userId) && userId > 0,
			refetchOnMount: 'always',
			staleTime: 0,
		},
	);

	const currentAvailability: UserAvailability[] = useMemo(
		() => extractUserAvailabilityFromUser(userData),
		[userData],
	);

	useEffect(() => {
		if (jobCodesInitialized || userJobCodesLoading) return;
		if (!Number.isFinite(userId) || userId <= 0) return;
		const ids = userJobCodes
			.map((row) => getJobCodeIdFromUserJobCode(row))
			.filter((id): id is number => id != null);
		setSelectedJobCodeIds(ids);
		setJobCodesInitialized(true);
	}, [userJobCodes, userJobCodesLoading, userId, jobCodesInitialized]);

	const initialValues = useMemo(
		() => buildOnboardingAvailabilityInitialValues(allDays, currentAvailability),
		[allDays, currentAvailability],
	);

	const formikRemountKey = useMemo(() => {
		const dayIds = allDays.map((day) => day.id ?? day['@id'] ?? day.label).join(',');
		const availabilityFingerprint = currentAvailability
			.map(
				(row) =>
					`${row.id}:${typeof row.day === 'string' ? row.day : row.day?.['@id'] || ''}:${row.startAt}:${row.endAt}`,
			)
			.join('|');
		return `${dayIds}::${availabilityFingerprint}`;
	}, [allDays, currentAvailability]);

	const handleSubmit = async (values: typeof initialValues) => {
		if (!editable) return;

		const enabledDays = values.availability.filter((d) => d.enabled);
		if (enabledDays.length === 0) {
			showToast('error', 'Please select at least one day you are available');
			return;
		}

		const invalidDays = values.availability.filter(
			(d) => d.enabled && (!d.startTime || !d.endTime),
		);
		if (invalidDays.length > 0) {
			const dayNames = invalidDays.map((d) => d.day.label).join(', ');
			showToast('error', `Please set start and end times for: ${dayNames}`);
			return;
		}

		// Overnight ranges (e.g. 10:00 PM – 2:00 AM) are valid — matching AvailabilityForm / Scheduling.

		const userId = Number(userData?.id);
		if (!Number.isFinite(userId) || userId <= 0) {
			showToast('error', 'Unable to save: user profile not loaded');
			return;
		}

		try {
			setIsSubmitting(true);
			// Job codes are manager-assigned only — employees cannot change them here.
			await saveOnboardingAvailability({
				workplaceId,
				days: values.availability,
				currentAvailability,
			});
			await queryClient.invalidateQueries(['user-availability']);
			await refetchUser();
			showToast('success', 'Availability saved successfully!');
			onSubmit();
		} catch (error: any) {
			console.error('Error saving onboarding availability:', error);
			showToast(
				'error',
				error?.response?.data?.message ||
					error?.message ||
					'Failed to save availability. Please try again.',
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const alreadySaved = hasSavedAvailability(currentAvailability);
	const isLoading = daysLoading || userLoading || jobCodesLoading || userJobCodesLoading;

	return (
		<Paper sx={{ p: 3 }}>
			<Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
				Set Your Availability
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Confirm the days and times you are typically available. Job Codes are assigned by your
				manager and cannot be changed here.
			</Typography>

			{isLoading ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={24} />
					<Typography sx={{ ml: 2 }}>Loading availability options...</Typography>
				</Box>
			) : (
				<Formik
					key={formikRemountKey}
					initialValues={initialValues}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({ values, setFieldValue, handleSubmit: formikSubmit }) => (
						<Form>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								{selectedJobCodeIds.length > 0 && (
									<Box sx={{ mb: 3 }}>
										<EligibleJobCodesPicker
											jobCodes={jobCodesData}
											selectedIds={selectedJobCodeIds}
											onChange={() => undefined}
											disabled
											helperText="Job Codes assigned by your manager (read-only)."
										/>
									</Box>
								)}

								{alreadySaved && (
									<CurrentAvailabilityList
										days={allDays}
										currentAvailability={currentAvailability}
										subtitle="Your saved availability (you can update it below)"
									/>
								)}

								<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
									Weekly Availability
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Select the days you are available and specify your preferred time range.
								</Typography>

								{values.availability.map((d, index) => (
									<Grid
										container
										spacing={2}
										alignItems="center"
										key={d.day['@id'] || d.day.id}
										sx={{ mb: 1, borderRadius: 1, p: 1 }}
									>
										<Grid item xs={12} sm={4}>
											<FormControlLabel
												control={
													<Checkbox
														checked={d.enabled}
														disabled={!editable || isSubmitting}
														onChange={(e) => {
															const updated = [...values.availability];
															updated[index] = {
																...updated[index],
																enabled: e.target.checked,
															};

															if (!e.target.checked) {
																updated[index].startTime = null;
																updated[index].endTime = null;
															} else {
																const lastFilledDay = [...updated]
																	.slice(0, index)
																	.reverse()
																	.find(
																		(day) =>
																			day.enabled && day.startTime && day.endTime,
																	);
																if (lastFilledDay) {
																	updated[index].startTime = dayjs(
																		lastFilledDay.startTime,
																	);
																	updated[index].endTime = dayjs(lastFilledDay.endTime);
																}
															}
															setFieldValue('availability', updated);
														}}
													/>
												}
												label={d.day.label}
											/>
										</Grid>
										<Grid item xs={6} sm={4}>
											<TimePicker
												label="Start Time"
												ampm
												format="hh:mm A"
												value={d.startTime}
												disabled={!editable || !d.enabled || isSubmitting}
												onChange={(newTime) => {
													const updated = [...values.availability];
													updated[index] = {
														...updated[index],
														startTime: newTime,
													};
													setFieldValue('availability', updated);
												}}
												slotProps={{
													textField: { fullWidth: true },
												}}
											/>
										</Grid>
										<Grid item xs={6} sm={4}>
											<TimePicker
												label="End Time"
												ampm
												format="hh:mm A"
												value={d.endTime}
												disabled={!editable || !d.enabled || isSubmitting}
												onChange={(newTime) => {
													const updated = [...values.availability];
													updated[index] = {
														...updated[index],
														endTime: newTime,
													};
													setFieldValue('availability', updated);
												}}
												slotProps={{
													textField: { fullWidth: true },
												}}
											/>
										</Grid>
									</Grid>
								))}

								{editable && (
									<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
										<Button
											type="button"
											disabled={isSubmitting}
											className={style.SubmitButton}
											style={{ padding: '5px', borderRadius: '5px' }}
											onClick={() => formikSubmit()}
										>
											{isSubmitting ? <CircularProgress size={24} /> : 'Save & Continue'}
										</Button>
									</Box>
								)}
							</LocalizationProvider>
						</Form>
					)}
				</Formik>
			)}
		</Paper>
	);
}
